/**
 * Pending Writes Indicator
 *
 * The web/desktop counterpart to mobile's offline banner. There is no app-level
 * banner on web — the established convention here is the compact connection pill
 * (UserMenu's dot, the notes SaveStatusIndicator) — so this follows that shape:
 * a small chip that appears only when there is something to say, and opens a
 * list of exactly what is waiting.
 *
 * It renders nothing when the queue is empty and the connection is fine, so it
 * costs nothing on the normal path.
 */
import { useSyncExternalStore } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useOfflineRecoveryCopies } from '@/hooks/useOfflineRecoveryCopies'
import { usePendingWrites } from '@/hooks/usePendingWrites'
import { describeOfflineWriteStatus } from '@/lib/offline/queue-actions'
import {
  canRetryOfflineWrite,
  isOfflineRecordRevision,
  needsOfflineDecision,
  nonRetryableOfflineWriteMessage,
} from '@/lib/offline/recovery'
import { cn } from '@/lib/utils'
import { clockPattern, formatDateUserTz } from '@/utils/date'
import { onlineManager } from '@tanstack/react-query'
import { CloudOff, CloudUpload } from 'lucide-react'

function subscribeOnline(listener: () => void) {
  return onlineManager.subscribe(listener)
}

function useIsOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => onlineManager.isOnline(),
    () => true,
  )
}

function formatQueuedAt(timestamp: number): string {
  const minutes = Math.round((Date.now() - timestamp) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

export function PendingWritesIndicator({ className }: { className?: string }) {
  const isOnline = useIsOnline()
  const recovery = useOfflineRecoveryCopies()
  const hasRecovery =
    !!recovery.status || !!recovery.error || recovery.copies.length > 0
  const { pending, blockedCount, retry, discard, organizationId, actionError } =
    usePendingWrites()

  // Nothing waiting and the connection is fine — say nothing at all.
  if (isOnline && pending.length === 0 && !actionError && !hasRecovery)
    return recovery.dialog ?? null

  const label = hasRecovery
    ? 'Review saved changes'
    : actionError
      ? 'Review sync status'
      : !isOnline
        ? pending.length > 0
          ? `Offline — ${pending.length} waiting`
          : 'Offline'
        : blockedCount > 0
          ? `${blockedCount} need${blockedCount === 1 ? 's' : ''} attention`
          : `${pending.length} waiting to sync`

  return (
    <>
      {recovery.dialog}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            disabled={pending.length === 0 && !actionError && !hasRecovery}
            className={cn(
              'h-8 gap-1.5 rounded-full px-3 text-[length:var(--font-size-sm)] font-medium',
              blockedCount > 0
                ? 'text-amber-600 dark:text-amber-500'
                : 'text-muted-foreground',
              className,
            )}
            aria-label={`${label}. Open the list of changes waiting to sync.`}
          >
            {isOnline ? (
              <CloudUpload className="h-3.5 w-3.5" />
            ) : (
              <CloudOff className="h-3.5 w-3.5" />
            )}
            <span>{label}</span>
          </Button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-80 p-0">
          <div className="border-b border-border/60 px-3 py-2">
            <p className="text-sm font-semibold">Waiting to sync</p>
            <p className="text-xs text-muted-foreground">
              Changes stay here until their outcome is confirmed. Switch to
              their original workspace to send them.
            </p>
            {actionError && (
              <p
                role="alert"
                className="mt-2 text-xs text-amber-600 dark:text-amber-500"
              >
                {actionError}
              </p>
            )}
          </div>

          {hasRecovery && (
            <section
              aria-label="Local recovery copies"
              className="border-b border-border/60 px-3 py-2"
            >
              <p className="text-sm font-semibold">Local recovery copies</p>
              <p className="mt-1 text-xs text-muted-foreground">
                These copies are kept separately and are never sent
                automatically. Check the current record before creating another
                change.
              </p>
              {recovery.status && (
                <p role="status" className="mt-2 text-xs">
                  {recovery.status.message}
                </p>
              )}
              {recovery.error && (
                <p role="alert" className="mt-2 text-xs text-destructive">
                  {recovery.error}
                </p>
              )}
              {recovery.status && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2"
                  disabled={recovery.busy || recovery.status.state === 'saving'}
                  onClick={() => void recovery.reload()}
                >
                  {recovery.status.state === 'failed'
                    ? 'Save recovery copy and reload'
                    : 'Reload saved changes'}
                </Button>
              )}
              {recovery.error && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={recovery.busy}
                  onClick={() => void recovery.refresh()}
                >
                  Retry recovery list
                </Button>
              )}
              <ul className="mt-2 max-h-48 space-y-2 overflow-auto">
                {recovery.copies.map(copy => (
                  <li key={copy.operationId}>
                    <p className="text-xs">
                      Copy from{' '}
                      {formatDateUserTz(
                        new Date(copy.createdAt),
                        `MMM d, yyyy ${clockPattern()}`,
                      )}
                    </p>
                    <div className="mt-1 flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={recovery.busy}
                        onClick={() => void recovery.download(copy)}
                      >
                        Download copy
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={recovery.busy}
                        onClick={() => void recovery.remove(copy)}
                      >
                        Remove copy
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <ScrollArea className="max-h-80">
            <ul className="divide-y divide-border/40">
              {pending.map(entry => (
                <li key={entry.envelope.idempotencyKey} className="px-3 py-2.5">
                  <p className="truncate text-sm">{entry.envelope.label}</p>
                  <p
                    className={cn(
                      'mt-0.5 text-xs',
                      needsOfflineDecision(entry)
                        ? 'text-amber-600 dark:text-amber-500'
                        : 'text-muted-foreground',
                    )}
                  >
                    {entry.status === 'queued' &&
                    entry.envelope.organizationId !== organizationId
                      ? 'Waiting for its original workspace'
                      : describeOfflineWriteStatus(entry)}{' '}
                    · {formatQueuedAt(entry.envelope.enqueuedAt)}
                  </p>

                  {needsOfflineDecision(entry) &&
                    !canRetryOfflineWrite(entry) && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {entry.status === 'conflicted' ||
                        (entry.envelope.kind === 'task.status' &&
                          !isOfflineRecordRevision(
                            entry.envelope.recordRevision,
                          ))
                          ? nonRetryableOfflineWriteMessage(entry)
                          : 'Too old to send safely. Discard it and create a new change.'}
                      </p>
                    )}

                  {needsOfflineDecision(entry) && (
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-7 text-xs"
                        disabled={
                          !isOnline ||
                          !canRetryOfflineWrite(entry) ||
                          entry.envelope.organizationId !== organizationId
                        }
                        onClick={() => void retry(entry)}
                      >
                        Send again
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-muted-foreground"
                        onClick={() => void discard(entry)}
                      >
                        Discard
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </ScrollArea>

          {blockedCount > 0 && (
            <div className="border-t border-border/60 px-3 py-2">
              <Badge variant="outline" className="text-[11px] font-normal">
                {blockedCount} could not be sent automatically
              </Badge>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </>
  )
}
